/* LcdDisplay — reusable 7-segment digital calculator display.
 *
 * Renders a value as glowing amber LCD digits with a ghost "8888" backdrop,
 * brushed metal bezel, inset screen, and subtle scanline overlay.
 *
 * Usage:
 *   <LcdDisplay value={94} digits={3} />
 *   <LcdDisplay value={742} digits={4} suffix="/1000" />
 */
import { useEffect, useState } from "react";

let _dsegLoaded = false;
function ensureDsegFont() {
  if (_dsegLoaded || typeof document === "undefined") return;
  _dsegLoaded = true;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://cdn.jsdelivr.net/npm/dseg@0.46.0/css/dseg.min.css";
  document.head.appendChild(link);
}

function useCountUp(target, durationMs = 1400) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return n;
}

export default function LcdDisplay({
  value = 0,
  digits = null,           // forces the ghost-segment width; auto if null
  suffix = null,           // e.g. "/1000"
  size = "md",             // "sm" | "md" | "lg"
  countUp = true,
  bare = false,            // remove bezel + screen — just the glowing digits
}) {
  useEffect(() => { ensureDsegFont(); }, []);
  const animated = useCountUp(value);
  const n = countUp ? animated : value;
  const width = digits ?? Math.max(String(value).length, 2);
  const visible = String(n).padStart(width, " ");
  const ghost = "8".repeat(width);

  // Bare mode — naked digits, no calculator frame (for use inside other shells
  // like the circular score ring where a rectangular bezel would clash).
  if (bare) {
    return (
      <span className={`bc-lcd-bare bc-lcd-${size}`} data-testid="lcd-display">
        <span className="bc-lcd-wrap" aria-label={`${n}${suffix || ""}`}>
          <span className="bc-lcd-ghost" aria-hidden>{ghost}</span>
          <span className="bc-lcd-active">{visible}</span>
          {suffix && <span className="bc-lcd-suffix">{suffix}</span>}
        </span>
        <style>{lcdStyles}</style>
      </span>
    );
  }

  return (
    <span className={`bc-lcd-bezel bc-lcd-${size}`} data-testid="lcd-display">
      <span className="bc-lcd-screen">
        <span className="bc-lcd-scanlines" aria-hidden />
        <span className="bc-lcd-wrap" aria-label={`${n}${suffix || ""}`}>
          <span className="bc-lcd-ghost" aria-hidden>{ghost}</span>
          <span className="bc-lcd-active">{visible}</span>
          {suffix && <span className="bc-lcd-suffix">{suffix}</span>}
        </span>
      </span>
      <style>{lcdStyles}</style>
    </span>
  );
}

const lcdStyles = `
        .bc-lcd-bezel {
          display: inline-block;
          padding: 8px 12px;
          background: linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #050505 100%);
          border: 1px solid rgba(212,175,55,0.35);
          border-radius: 6px;
          box-shadow:
            inset 0 2px 8px rgba(0,0,0,0.9),
            inset 0 -1px 2px rgba(212,175,55,0.15),
            0 0 30px rgba(212,175,55,0.18),
            0 2px 4px rgba(0,0,0,0.6);
        }
        .bc-lcd-screen {
          position: relative;
          display: inline-block;
          background: radial-gradient(ellipse at center, #0d0905 0%, #050302 100%);
          padding: 8px 14px 6px;
          border-radius: 3px;
          box-shadow:
            inset 0 0 24px rgba(0,0,0,0.95),
            inset 0 1px 2px rgba(0,0,0,1);
          overflow: hidden;
        }
        .bc-lcd-scanlines {
          position: absolute;
          inset: 0;
          background-image: repeating-linear-gradient(
            to bottom,
            rgba(255,255,255,0.03) 0px,
            rgba(255,255,255,0.03) 1px,
            transparent 1px,
            transparent 3px
          );
          pointer-events: none;
          mix-blend-mode: overlay;
        }
        .bc-lcd-wrap {
          position: relative;
          display: inline-block;
          font-family: "DSEG7-Classic", "DSEG7 Classic", "Share Tech Mono", "JetBrains Mono", ui-monospace, monospace;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0.05em;
          white-space: pre;
        }
        .bc-lcd-sm .bc-lcd-wrap { font-size: 1.6rem; }
        .bc-lcd-md .bc-lcd-wrap { font-size: 2.2rem; }
        .bc-lcd-lg .bc-lcd-wrap { font-size: 2.6rem; }
        @media (min-width: 640px) {
          .bc-lcd-sm .bc-lcd-wrap { font-size: 1.8rem; }
          .bc-lcd-md .bc-lcd-wrap { font-size: 2.6rem; }
          .bc-lcd-lg .bc-lcd-wrap { font-size: 3.4rem; }
        }
        @media (min-width: 1024px) {
          .bc-lcd-md .bc-lcd-wrap { font-size: 2.8rem; }
          .bc-lcd-lg .bc-lcd-wrap { font-size: 3.8rem; }
        }
        .bc-lcd-ghost {
          color: rgba(212,175,55,0.09);
          user-select: none;
        }
        .bc-lcd-active {
          position: absolute;
          inset: 0;
          color: #F5C842;
          text-shadow:
            0 0 6px rgba(245,200,66,0.85),
            0 0 14px rgba(245,200,66,0.55),
            0 0 28px rgba(212,175,55,0.35);
        }
        .bc-lcd-suffix {
          position: relative;
          font-family: "JetBrains Mono", ui-monospace, monospace;
          font-size: 0.4em;
          color: rgba(245,200,66,0.55);
          margin-left: 6px;
          letter-spacing: 0.05em;
          vertical-align: super;
        }
        /* Bare mode — just glowing digits, no bezel */
        .bc-lcd-bare { display: inline-block; }
`;
